import type {
  ExteriorFinishEdgeOpeningSide,
  ExteriorFinishEdgeType,
} from "building-types/exteriorFinishEdgeAttributeTypes";
import { clamp } from "common/utils/math";
import radianToDegree from "common/utils/radianToDegree";
import { faceCentroid } from "geom/brep";
import type { Face } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { line2Normal, type Line2Segment } from "geom/line2";
import { planeEvalDir, planeIsCoplanar, planeNormal } from "geom/plane";
import { vec3AngleBetween, vec3Dot } from "geom/vec3";

interface ExteriorFinishEdgeAngleAndType {
  readonly angleDegrees: number;
  readonly edgeType: ExteriorFinishEdgeType;
}

// Angle of 180° means the two faces are coplanar; 90° is a right-angle corner.
// edgeType distinguishes whether the building bulges outward or indents at the edge:
// the projection of (centroidB - centroidA) onto (normalA - normalB) is positive at
// an inside (concave) corner, where each face's centroid lies on the outward side of
// the other face's plane.
export function computeExteriorFinishEdgeAngleAndType(faceA: Face, faceB: Face): ExteriorFinishEdgeAngleAndType | null {
  const centroidA = faceCentroid(faceA);
  if (centroidA === null) {
    return null;
  }
  const [cax, cay, caz] = centroidA;

  const centroidB = faceCentroid(faceB);
  if (centroidB === null) {
    return null;
  }
  const [cbx, cby, cbz] = centroidB;

  if (planeIsCoplanar(faceA.plane, faceB.plane)) {
    return { angleDegrees: 180, edgeType: "COPLANAR" };
  }

  const [nax, nay, naz] = planeNormal(faceA.plane);
  const [nbx, nby, nbz] = planeNormal(faceB.plane);

  const normalAngleRadians = vec3AngleBetween(nax, nay, naz, nbx, nby, nbz);
  const angleDegrees = clamp(180 - radianToDegree(normalAngleRadians), 0, 180);

  const projection = vec3Dot(cbx - cax, cby - cay, cbz - caz, nax - nbx, nay - nby, naz - nbz);

  const edgeType: ExteriorFinishEdgeType = projection > 0 ? "INSIDE_CORNER" : "OUTSIDE_CORNER";

  return { angleDegrees, edgeType };
}

export function computeExteriorFinishEdgeOpeningSide(
  openingFace: Face,
  openingEdgeSegment: Line2Segment,
  tolerance: number = DIST_EPSILON,
): ExteriorFinishEdgeOpeningSide {
  const [normalX, normalY] = line2Normal(...openingEdgeSegment);
  const [, , normalZ] = planeEvalDir(openingFace.plane, normalX, normalY);
  if (normalZ > tolerance) {
    return "TOP";
  }

  if (normalZ < -tolerance) {
    return "BOTTOM";
  }

  return "SIDE";
}
